/**
 * Phase 4b PR 5 — `useAthleteContext` hook.
 *
 * **Phase 6b1.5 PR 2 (2026-05-10): refactored to a Context Provider.**
 * Before this refactor, `useAthleteContext` was a plain hook with
 * local `useState` — each call created an INDEPENDENT state instance.
 * This caused a cross-cutting bug where modal-driven saves (Location,
 * Body Metrics, etc.) and inline saves (Profile fields, Preferences
 * toggles) updated `useSettingsPersist`'s internal hook instance via
 * `mergeContext`, but the route-level instance held by `settings.tsx`
 * stayed stale — so the UI never reflected the new values until the
 * athlete manually refreshed the page.
 *
 * The Provider pattern fixes this: one state instance lives at the
 * top of the app; all `useAthleteContext()` consumers read/write the
 * SAME state via React Context. No per-section reload-pipe needed.
 *
 * Loads `athlete_context.context_data` from Supabase on mount, merges
 * extracted-context updates from chat-stream completion frames, and
 * persists changes back to Supabase. Mirrors the vanilla pattern at
 * `src/renderer/js/athlete-context.js`.
 *
 * Output is the deduplicated pill list (via `dedupePills`) — fixes
 * tech-debt #0b (duplicate Shared Context pills).
 *
 * Cross-surface parity: vanilla and React read/write the same
 * `athlete_context` row (PK: user_id), so switching surfaces preserves
 * extracted context (until Phase 7 cutover, when vanilla retires).
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useAuthContext } from '../contexts/AuthContext';
import {
  dedupePills,
  isWeeklyMileageBandKey,
  isWeeklyMileageFigureKey,
  isWeeklyMileageRepeatKey,
  type ContextPill,
} from '../lib/dedupePills';
import { apiUrl } from '../lib/apiBase';

// UX-S2 F2 — debounce window for coalescing context writes (see `persist`).
const PERSIST_DEBOUNCE_MS = 300;

/**
 * ⚖️ B243 (2026-09-23) — THE BOUNDED RETRY AFTER `context_read_unavailable`.
 *
 * B236's 503 (`handlePostContext` refusing a merge against a prior read it
 * knows failed) is retryable infrastructure trouble: nothing was written.
 * Before this fix `flushPersist` cleared `pendingWriteRef` before the POST
 * and its catch only logged, so the 503 silently dropped the batch — local
 * state kept showing it saved (Opus sign-off finding 4, strictly better than
 * the row wipe B236 replaced, but still a loss). Two fixed delays from the
 * failure, mirroring `useAccountRole.ts`'s `AMBIGUOUS_HOLD_BACKOFF_MS` (the
 * same day's precedent for "a bounded backoff"): after both are spent the
 * batch is dropped exactly as any other persist failure is, logged not
 * thrown.
 */
const CONTEXT_READ_UNAVAILABLE_RETRY_MS = [5_000, 15_000] as const;

/**
 * ⚖️ B243 (harvest 2026-09-23b finding 1) — THE OWNER TAG.
 *
 * The requeue above puts a failed batch back and arms a timer that calls
 * `flushPersistRef.current` — which, after an in-place account switch (a
 * cross-tab `SIGNED_IN`, a native `setSession`, a sign-out then a sign-in
 * inside the backoff), closes over the NEW `userId` and the NEW session
 * token. With no owner on the batch, that timer posted account A's queued
 * patch to account B's `/api/context/:userId` row under B's own token.
 * Every batch now records the `userId` that queued it; `flushPersist` sends
 * a batch only to its own owner and only while that owner is still the
 * current `userId` — any other batch is dropped, logged once, never sent.
 */
interface PendingWrite {
  patch: Record<string, unknown>;
  removeKeys: Set<string>;
  /** The `userId` that queued this batch. Checked at flush time and at
   *  requeue time against the CURRENT `userId` — never assumed still true. */
  owner: string;
}

/**
 * Merge `incoming` onto `base`, last-writer-wins: a key `incoming` sets
 * overrides the same key in `base` (nested `coach_visible` / `coach_private`
 * merge one level deep); a key `incoming` removes drops out of the patch and
 * into `removeKeys`; a key `incoming` sets is no longer pending removal.
 * Shared by `persist` (base = the current pending write, incoming = a fresh
 * caller edit) and B243's requeue (base = a failed batch, incoming =
 * whatever enqueued while that batch was in flight — so a newer key or
 * removal wins there too). `owner` always comes from `base`: a fresh caller
 * edit merges INTO the current owner's batch, and a requeue keeps the
 * failed batch's original owner (harvest 2026-09-23b finding 1).
 */
function mergePendingWrite(
  base: PendingWrite,
  incoming: { patch?: Record<string, unknown>; removeKeys?: string[] },
): PendingWrite {
  const patch: Record<string, unknown> = { ...base.patch };
  if (isPlainObject(incoming.patch)) {
    for (const [k, v] of Object.entries(incoming.patch)) {
      if ((k === 'coach_visible' || k === 'coach_private') && isPlainObject(v)) {
        const existing = isPlainObject(patch[k]) ? (patch[k] as Record<string, unknown>) : {};
        patch[k] = { ...existing, ...(v as Record<string, unknown>) };
      } else {
        patch[k] = v;
      }
    }
  }
  const removeKeys = new Set(base.removeKeys);
  if (isPlainObject(incoming.patch)) {
    for (const k of Object.keys(incoming.patch)) removeKeys.delete(k);
  }
  if (Array.isArray(incoming.removeKeys)) {
    for (const k of incoming.removeKeys) {
      if (typeof k === 'string') {
        removeKeys.add(k);
        delete patch[k];
      }
    }
  }
  return { patch, removeKeys, owner: base.owner };
}

/**
 * ⚖️ B47 fix 6 (B127 + B134) — THE DEADLINE ON A CONTEXT READ.
 *
 * A read that THROWS has always settled this hook (`loadFailed`). A read that
 * simply never answers had no end at all: `loading` stayed true, and with it
 * `useAccountRole().boundaryPending`, so `AthleteSurfaceGuard` held every
 * guarded route on an empty busy element — up to 60 s on WKWebView, or until
 * the athlete killed the app. B127 is her side of that (no season, no CTA, no
 * retry surface, the forever-skeleton). This deadline ends the read: it settles
 * as a FAILED read, `loadFailed` opens the retry surface, and the hold lifts
 * into something with a control on it.
 *
 * ⚠️ WHAT THIS DEADLINE DOES AND DOES NOT BOUND (round-36 critic F1 + F4, a
 * correction of what this comment used to claim). The hold has three terms and
 * this bounds ONE of them:
 *
 *   · the context read — here, at 10 s;
 *   · the role fetch (`useAccountRole.ts`) and the season read
 *     (`useActiveSeason.ts`) — bounded at the same 10 s by B47 fix 8;
 *   · the auth boot's own `getSession()` (`contexts/AuthContext.tsx`) — NOT
 *     bounded. While it runs `userId` is null, `reload` takes its early return
 *     and arms no deadline at all, so the blank outlives every one of these.
 *     That is B147, filed, and it is not this file.
 *
 * And a dead connection is not the case this fires on: supabase-js ends that
 * read itself, about 3 s BEFORE the deadline. What the deadline reaches is the
 * socket that is accepted and never answered (B127), which had no end at all.
 *
 * ⚠️ IT DOES NOT THROW, EITHER. postgrest-js with no `.throwOnError()` —
 * nothing in `src/app/src` calls it — RESOLVES a failed request as
 * `{ data: null, error, status: 0 }`. The `if (error) throw error` below is the
 * conversion, and it is the only reason the catch path ever sees anything
 * (round-46 critic S3, driven against the real client). So a LATE answer has to
 * be inspected for `error`; catching it is not enough.
 *
 * ⏱️ WHY 10 s AND NOT LESS. supabase-js retries its own fetch with backoff
 * before it gives up — attempts MEASURED at 0.24, 1.25, 3.25 and 7.26 s, giving
 * up at about 7.27 s (the round-36 UX measurement, the ladder the test files
 * carry). An earlier version of this comment quoted 2.4 / 3.4 / 5.4 / 9.4 s from
 * B134 and concluded that 10 s sat just past the last backoff; the two ladders
 * cannot both be right, and the round-36 one is the one that was driven
 * (round-46 critic S7). The last attempt has about 3 s left before this
 * deadline, so a connection that is merely slow still resolves the ordinary way
 * and only a read that never lands at all meets the deadline.
 *
 * 🔁 AND IT COSTS ONE REQUEST, NOT TWO. `.maybeSingle()` returns a BUILDER — a
 * thenable that sends its request inside `then`, once per call, with no
 * memoisation (`postgrest-js/src/PostgrestBuilder.ts`). Racing the builder and
 * then awaiting it again sent the query TWICE and observed only the second, so
 * the row the first read would have answered with was never seen and two
 * sockets leaked per deadline (round-46 critic S1). The builder is resolved
 * ONCE below and that one promise is shared by the race and the late await.
 *
 * ⛔ READS ONLY. A write (`persist` / `flushPersist`) has no deadline: an
 * abandoned write is a write whose outcome nobody knows, and its caller has
 * already applied it optimistically.
 */
const READ_DEADLINE_MS = 10_000;

/** The race token for the deadline above. A symbol, so nothing a read could
 *  return can be mistaken for it. */
const READ_DEADLINE = Symbol('athlete-context-read-deadline');

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return v != null && typeof v === 'object' && !Array.isArray(v);
}

export interface UseAthleteContextResult {
  /** Deduplicated pill list ready for SharedContextAccordion / ContextModal. */
  pills: ContextPill[];
  /** Raw context_data — exposed for components that need underscore-prefixed fields. */
  raw: Record<string, unknown>;
  /** True until the initial Supabase load resolves. */
  loading: boolean;
  /**
   * P5 offline boot — true when the last load THREW (offline / network),
   * so `raw` is empty-because-unknown, NOT empty-because-new-athlete.
   * Gates that route on missing context (the Today onboarding gate) must
   * treat a failed load as unknown and stay put — a killed shell
   * relaunched in airplane mode was sent to ONBOARDING as a "new user"
   * (founder device test 2026-07-09).
   *
   * ⚖️ B47 fix 6 — and true for a read that never answered either. A read
   * abandoned at `READ_DEADLINE_MS` is a FAILED read, never a silent success:
   * it is the same unknown, reached by a different road (B127, B134).
   *
   * ⚖️ B47 fix 8 — and it goes back to false if that read answers after all.
   * The deadline abandons the read, it does not cancel it, so a row landing at
   * 12 s is a row that LOADED: it lands, this clears, and the retry surface
   * gives way to the page instead of repeating "didn't load" on every press
   * (round-36 UX 1).
   */
  loadFailed: boolean;
  /**
   * ⚖️ B47 fix 9 (round-44 critic F1, B160) — WHOSE ROW `raw` IS, PUBLISHED.
   *
   * True only when the row in `raw` was read for the CURRENT `userId`. Fix 6b
   * already tracked that internally (`rawFor`), but a ref cannot be read from a
   * render, so consumers had no way to tell "this account's context" from "the
   * previous account's context, one commit after the switch". `useCoachMode`
   * needs exactly that: an in-place switch lands one commit BEFORE the new
   * account's read starts, so on that commit `loading` is still false and `raw`
   * is still the previous account's — and the coach answer derived from it was
   * being filed under the new uid and shown to her for the whole of her read.
   *
   * False in all four of: never read, a read in flight for a new account, the
   * commit the switch lands on, and after a read that failed (the same states
   * in which `rawFor` is null). True after a settle for this account; a later
   * `mergeContext` or `removePill` writes onto that same row and leaves the
   * stamp where the read put it.
   */
  rawIsMine: boolean;
  /**
   * Merge new fields (e.g., from chat-stream completion frames). Persists
   * to Supabase asynchronously. Caller must NOT await for correctness.
   */
  mergeContext: (updates: Record<string, unknown>) => void;
  /** Remove a pill by canonical key. */
  removePill: (key: string) => void;
  /** Force a reload from Supabase (e.g., after sign-in). */
  reload: () => Promise<void>;
}

const EMPTY_RESULT: UseAthleteContextResult = {
  pills: [],
  raw: {},
  loading: false,
  loadFailed: false,
  rawIsMine: false,
  mergeContext: () => {},
  removePill: () => {},
  reload: async () => {},
};

const AthleteContextContext = createContext<UseAthleteContextResult>(EMPTY_RESULT);

// ─────────────────────────────────────────────────────────────
// Provider (single source of truth)
// ─────────────────────────────────────────────────────────────

export interface AthleteContextProviderProps {
  children: ReactNode;
}

/**
 * Wraps the app — must mount inside `<AuthProvider>` (depends on
 * supabase + user). Single instance per app; all consumers via
 * `useAthleteContext()` share the same state.
 */
export function AthleteContextProvider({
  children,
}: AthleteContextProviderProps): JSX.Element {
  const { supabase, user } = useAuthContext();
  const userId = user?.id ?? null;

  const [raw, setRaw] = useState<Record<string, unknown>>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [loadFailed, setLoadFailed] = useState<boolean>(false);
  const rawRef = useRef(raw);
  rawRef.current = raw;
  /**
   * ⚖️ B243 (harvest 2026-09-23b finding 1) — THE CURRENT OWNER, BY REF.
   * `requeueAfterReadUnavailable` decides whether a failed batch is still
   * postable from inside an async 503 handler, whose own closure can be
   * arbitrarily stale (it was created back when the batch was queued). A
   * ref, updated on every render, is the only way that check sees the
   * `userId` that is current NOW rather than the one it closed over then.
   */
  const userIdRef = useRef(userId);
  userIdRef.current = userId;
  /**
   * ⚖️ B47 fix 6 — WHICH READ MAY STILL SPEAK. The deadline abandons a read
   * without cancelling the request behind it, so a read the deadline gave up on
   * can still land minutes later, on top of a newer read's answer (Today's
   * "Try again" starts exactly such a newer read). Every read carries the
   * sequence it began at and only the newest one writes state.
   */
  const readSeq = useRef(0);
  /**
   * ⚖️ B47 fix 6b (round-36 security F1) — WHOSE ROW `raw` IS. The provider
   * mounts once (`main.tsx`) and is not keyed by user, and an in-place account
   * switch (a cross-tab `SIGNED_IN`, a native `setSession`) changes `userId`
   * under it with no remount. Before this ref, `raw` was cleared only on
   * sign-out and on a throw — so with fix 6's deadline the provider could
   * settle (`loading` false, `loadFailed` true) while still holding the
   * PREVIOUS account's context, and chat sent that blob under the new session.
   * This records the id whose row produced `raw`; null when `raw` belongs to
   * nobody (never read, cleared, or wiped by a failed read).
   *
   * ⚖️ B47 fix 9 (B160) — and it is MIRRORED IN STATE (`rawOwner`) so consumers
   * can read it. The ref stays the authority for the async checks below, which
   * have to see the newest value from inside a read; the state is what makes
   * the same fact available to a render (`rawIsMine` in the value). The two are
   * written together, on the same four lines, and never apart.
   */
  const rawFor = useRef<string | null>(null);
  const [rawOwner, setRawOwner] = useState<string | null>(null);
  const stampRawFor = useCallback((owner: string | null) => {
    rawFor.current = owner;
    setRawOwner(owner);
  }, []);

  // MAT-3.5 — persist through the server-authoritative endpoint instead of a
  // direct Supabase upsert. The endpoint MERGES the `patch` / `removeKeys`
  // delta onto the existing DB row, so a partial write from a stale/empty
  // in-memory `raw` can no longer clobber the whole context_data (the
  // destructive-overwrite data-loss bug). See
  // critical-bug-context-destructive-overwrite.md.
  //
  // ⚖️ B148 (2026-09-23) — PATCH-ONLY. This provider used to also send a
  // wholesale optimistic `context` blob so the server's flag-off path stayed
  // byte-identical. That branch is retired here: the body below carries only
  // `patch` / `removeKeys`, never a `context` key. The server's wholesale
  // `context` branch stays (frozen Builds 29/30 still send it); a body with no
  // `context` key always takes the merge path server-side (lane R1).
  //
  // UX-S2 F2 — coalesce writes. A single /v2/today load fires several context
  // writes (proactive `_last_dashboard_visit`, the onboarding-gate re-assert,
  // daily-motivation cache, chat extraction). During the empty-`raw` remount
  // window two can fire on the SAME tick, racing as concurrent merges on the
  // just-hardened row. We accumulate writes into a pending batch and flush ONE
  // merged POST after a short debounce. Optimistic local state (`setRaw`) is
  // unchanged + synchronous — only the network write is coalesced. A pending
  // batch is flushed on pagehide / unmount (with `keepalive`) so navigating away
  // mid-window never drops a write.
  const pendingWriteRef = useRef<PendingWrite | null>(null);
  const flushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // ⚖️ B243 — the retry state for a batch a 503 `context_read_unavailable`
  // just refused. `retryTimerRef` is the armed backoff timer; `retryCountRef`
  // is how many of `CONTEXT_READ_UNAVAILABLE_RETRY_MS` this batch has spent
  // (reset to 0 on any settle — a success, or a DIFFERENT failure, which
  // drops the batch exactly as before and leaves nothing to keep retrying).
  // `flushPersistRef` breaks the cycle: the retry timer needs to call
  // `flushPersist`, and `flushPersist` needs to call the requeue function
  // below it, so the requeue function cannot close over `flushPersist`
  // directly (`persist` → `flushPersist` is the same shape, via `useCallback`
  // dependency order — this pair calls back the other way, hence the ref).
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const retryCountRef = useRef(0);
  const flushPersistRef = useRef<(opts?: { keepalive?: boolean }) => Promise<void>>();

  // ⚖️ B243 — requeue a batch a 503 `context_read_unavailable` refused (the
  // server wrote nothing). The failed batch is the BASE; whatever enqueued
  // while that request was in flight is the INCOMING side of the same
  // last-writer-wins merge `persist` applies to its own pending write, so a
  // newer key or removal wins over the one being retried. Bounded: past
  // `CONTEXT_READ_UNAVAILABLE_RETRY_MS.length` attempts the batch is dropped,
  // logged by the caller exactly as any other persist failure is.
  //
  // ⚖️ B243 (harvest 2026-09-23b finding 1) — AND ONLY WHILE THE OWNER IS
  // STILL CURRENT. This runs from inside a 503 handler whose own closure can
  // be arbitrarily stale, so the check is against `userIdRef.current`, never
  // against a `userId` this closure captured at creation. A batch whose owner
  // has since switched away is dropped here, WITHOUT touching
  // `pendingWriteRef` — the current owner (if any) may already have its own
  // legitimate batch queued there, and merging a stale batch onto it would
  // both mislabel it and risk it being dropped too, at the flush check below,
  // for an owner mismatch that was never true of it.
  const requeueAfterReadUnavailable = useCallback((failedBatch: PendingWrite) => {
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
    if (failedBatch.owner !== userIdRef.current) {
      retryCountRef.current = 0;
      // eslint-disable-next-line no-console
      console.warn('[athlete-context] persist dropped: account switched before retry');
      return;
    }
    const attempt = retryCountRef.current;
    if (attempt >= CONTEXT_READ_UNAVAILABLE_RETRY_MS.length) {
      retryCountRef.current = 0;
      // eslint-disable-next-line no-console
      console.warn('[athlete-context] persist failed: context read unavailable, retries exhausted');
      return;
    }
    const newerPending = pendingWriteRef.current;
    pendingWriteRef.current = newerPending
      ? mergePendingWrite(failedBatch, {
          patch: newerPending.patch,
          removeKeys: [...newerPending.removeKeys],
        })
      : failedBatch;
    retryCountRef.current = attempt + 1;
    retryTimerRef.current = setTimeout(
      () => void flushPersistRef.current?.(),
      CONTEXT_READ_UNAVAILABLE_RETRY_MS[attempt],
    );
  }, []);

  const flushPersist = useCallback(
    async (opts?: { keepalive?: boolean }) => {
      if (flushTimerRef.current) {
        clearTimeout(flushTimerRef.current);
        flushTimerRef.current = null;
      }
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
      const pending = pendingWriteRef.current;
      pendingWriteRef.current = null;
      if (!pending || !supabase || !userId) return;
      // ⚖️ B243 (harvest 2026-09-23b finding 1) — THE FLUSH CHECK. This is the
      // instance `flushPersistRef.current` points to right now, so its
      // `userId` closure IS the current account — but `pending` can still be a
      // batch a retry timer requeued for a PREVIOUS one (an in-place switch
      // does not reach across an in-flight request or an armed backoff timer
      // to relabel it). A batch only ever posts to the account that queued it.
      if (pending.owner !== userId) {
        // eslint-disable-next-line no-console
        console.warn('[athlete-context] persist dropped: batch owner does not match the current account');
        return;
      }
      // B148 — patch-only body. No `context` key, ever.
      const body: Record<string, unknown> = { patch: pending.patch };
      if (pending.removeKeys.size > 0) body.removeKeys = [...pending.removeKeys];
      try {
        const { data: sessionData } = await supabase.auth.getSession();
        const token = sessionData?.session?.access_token;
        if (!token) throw new Error('no auth session');
        const res = await fetch(apiUrl(`/api/context/${userId}`), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify(body),
          keepalive: opts?.keepalive === true,
        });
        if (!res.ok) {
          // ⚖️ B243 — a 503 `context_read_unavailable` means the server wrote
          // nothing (B236's refusal of a merge against a prior read it knows
          // failed): requeue and retry rather than drop. Any other failure
          // (a different 503, 4xx, network) keeps today's behaviour below —
          // logged, not retried.
          if (res.status === 503) {
            let code: unknown;
            try {
              code = (await res.json())?.code;
            } catch {
              // non-JSON body — falls through to the generic failure below
            }
            if (code === 'context_read_unavailable') {
              requeueAfterReadUnavailable(pending);
              return;
            }
          }
          throw new Error(`context save failed: ${res.status}`);
        }
        retryCountRef.current = 0;
      } catch (err) {
        retryCountRef.current = 0;
        // eslint-disable-next-line no-console
        console.warn('[athlete-context] persist failed:', err);
      }
    },
    [supabase, userId, requeueAfterReadUnavailable],
  );
  flushPersistRef.current = flushPersist;

  // Enqueue a write into the pending batch (last-writer-wins per key) and (re)arm
  // the debounce. Fire-and-forget by design — callers never await it.
  //
  // ⚖️ B243 (harvest 2026-09-23b finding 1) — TAGGED WITH THE CURRENT OWNER.
  // An existing `pendingWriteRef` is only reused when it already belongs to
  // this `userId`; anything else (there should be none — the render-time
  // check below wipes a foreign leftover on every switch, before this
  // component's own effects even run — but belt-and-suspenders costs nothing
  // here) starts a fresh batch under this owner rather than merging into
  // someone else's.
  const persist = useCallback(
    (body: { patch?: Record<string, unknown>; removeKeys?: string[] }) => {
      if (!userId) return;
      const prev =
        pendingWriteRef.current && pendingWriteRef.current.owner === userId
          ? pendingWriteRef.current
          : { patch: {}, removeKeys: new Set<string>(), owner: userId };
      pendingWriteRef.current = mergePendingWrite(prev, body);
      if (flushTimerRef.current) clearTimeout(flushTimerRef.current);
      flushTimerRef.current = setTimeout(() => void flushPersist(), PERSIST_DEBOUNCE_MS);
    },
    [flushPersist, userId],
  );

  // ⚖️ B243 (harvest 2026-09-23b finding 1) — CLEAR ON EVERY OWNER CHANGE,
  // DURING RENDER. React's own "adjusting state (or refs) while rendering"
  // pattern: comparing a ref of the last `userId` seen against the current
  // one, and clearing SYNCHRONOUSLY in the render body rather than inside a
  // `useEffect`. An effect version of this raced the pagehide effect below
  // and LOST: React cleans up every changed effect before running any new
  // one (two full passes per commit, not paired per-hook), so the pagehide
  // cleanup's own `if (pendingWriteRef.current) flushPersist(...)` — which
  // already fires on every `userId` change today, since `flushPersist`'s
  // identity depends on `userId` too — ran on the OLD (still-matching)
  // owner's closure before an effect-based clear here ever got the chance to
  // empty `pendingWriteRef` first. Render happens strictly before ANY effect
  // for this commit, so doing it here is the only ordering that reliably
  // wins that race. Drops the pending batch, the armed retry timer, and its
  // attempt count for the account being left (including on sign-out, to
  // null). The flush check and the requeue-owner check are the backstop for
  // what even this cannot reach: a request already in flight, or a retry
  // timer that re-arms itself from a 503 landing after this has already run.
  const persistOwnerRef = useRef(userId);
  if (persistOwnerRef.current !== userId) {
    persistOwnerRef.current = userId;
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
    pendingWriteRef.current = null;
    retryCountRef.current = 0;
  }

  // Flush any pending write before the page goes away (navigation / close) and
  // on provider teardown, so the debounce window can't drop a write.
  useEffect(() => {
    const onHide = (): void => {
      if (pendingWriteRef.current) void flushPersist({ keepalive: true });
    };
    window.addEventListener('pagehide', onHide);
    return () => {
      window.removeEventListener('pagehide', onHide);
      if (pendingWriteRef.current) void flushPersist({ keepalive: true });
    };
  }, [flushPersist]);

  const reload = useCallback(async () => {
    if (!supabase || !userId) {
      // ⚖️ B47 fix 6b (round-36 security F2) — SIGN-OUT INVALIDATES A READ IN
      // FLIGHT. The token used to advance only past this return, so a read
      // started for the previous account still passed the `seq` check when it
      // landed and put that account's context back into provider memory while
      // the app was signed out — the exact input the F1 window needs at the
      // next in-place sign-in. Every transition advances it now, sign-out
      // included.
      readSeq.current += 1;
      stampRawFor(null);
      setRaw({});
      setLoading(false);
      // ⚖️ B47 fix 9 (round-46 security, Suspected 2) — AND THE FAILURE GOES
      // WITH THE SESSION. `loadFailed` used to survive a sign-out, so after a
      // deadline for A the next in-place sign-in as B opened on A's retry
      // surface. `raw` is `{}` by then, so nothing of A was shown; what B met
      // was a failure that was never hers.
      setLoadFailed(false);
      return;
    }
    const seq = (readSeq.current += 1);
    // ⚖️ B47 fix 6b (F1) — A ROW IN MEMORY BELONGS TO ONE ACCOUNT. When this
    // read is for someone other than whoever produced `raw`, the previous
    // account's context goes BEFORE the read starts, so no settle path — least
    // of all the deadline, which leaves `raw` as it found it — can leave it
    // live under the new session. Functional and identity-preserving: with
    // nothing to clear the same object comes back, so a first mount and every
    // same-account read commit exactly what they did before this fix.
    if (rawFor.current !== userId) {
      stampRawFor(null);
      setRaw((prev) => (Object.keys(prev).length === 0 ? prev : {}));
    }
    setLoading(true);
    // The deadline. Cleared in `finally`, so a read that answers in time leaves
    // no timer behind and behaves exactly as it did before this fix.
    let deadline: ReturnType<typeof setTimeout> | undefined;
    const deadlinePassed = new Promise<typeof READ_DEADLINE>((resolve) => {
      deadline = setTimeout(() => resolve(READ_DEADLINE), READ_DEADLINE_MS);
    });
    try {
      // B6 (2026-05-16) — also read the top-level columns so we can
      // backfill missing JSON Phase 7 signals for legacy users.
      // Pre-Phase-7-PR-1 users completed onboarding via a flow that
      // wrote to the top-level `athlete_context.onboarding_completed` /
      // `journey` / `parq_completed` columns. Phase 7 PR 1 ships the
      // canonical writer that populates `context_data._onboarding_*`,
      // `_journey`, `_parq_*` JSON fields. The Today gate +
      // Onboarding safety guard ONLY read the JSON, so legacy users
      // (Ramiro R, Lida Castro, etc.) were misrouted to /v2/onboarding
      // even though they had completed it years ago.
      //
      // 3 legacy rows were one-shot SQL-backfilled the same day this
      // fix shipped, but the in-memory fallback below catches any
      // future case where the columns drift (e.g. a partial write,
      // restored backup, or a coach manually flipping the boolean).
      //
      // ⚖️ B47 fix 6 — the query is BUILT synchronously here, inside the try,
      // exactly as it was before the deadline: a builder that throws on the
      // spot still runs catch and finally inside the caller's own batch, which
      // is the sequence fix 5 pinned (a press that reaches no read at all).
      //
      // ⚖️ B47 fix 9 (round-46 critic S1) — AND IT IS RESOLVED EXACTLY ONCE.
      // `.maybeSingle()` hands back a PostgrestBuilder: a thenable that sends
      // its request inside `then`, once per call, with nothing memoised. The
      // race below and the late `await` further down are two `then` calls, so
      // the query used to go out TWICE — the first read's answer was never
      // observed by anyone (round-36 UX 1 unfixed) and every deadline leaked a
      // second socket onto a host already failing to answer the first.
      // `Promise.resolve` calls `then` once and gives both awaits the same
      // promise.
      const builder = supabase
        .from('athlete_context')
        .select('context_data, onboarding_completed, journey, parq_completed')
        .eq('user_id', userId)
        .maybeSingle();
      const pending = Promise.resolve(builder);
      let settled = await Promise.race([pending, deadlinePassed]);
      // A read the deadline already gave up on, landing late: a newer read owns
      // this state now and its answer is the one on screen.
      if (seq !== readSeq.current) return;
      if (settled === READ_DEADLINE) {
        // ⚖️ B47 fix 6 — AN ABANDONED READ IS A FAILED READ (B127, B134).
        // `loadFailed`, so every consumer that routes on missing context reads
        // this as unknown and never as a new athlete (which is B47 itself), and
        // `raw` is left exactly as it was: the deadline gives up on an answer,
        // it does not throw away one already in hand.
        setLoadFailed(true);
        // ⚖️ B47 fix 6b (F1, the belt) — one in hand belongs to THIS account or
        // it goes with the failed read. An empty `raw` with `loadFailed` true is
        // the state the throw path already produces, so every consumer reads it
        // as unknown and never as a new athlete.
        if (rawFor.current !== userId) {
          setRaw((prev) => (Object.keys(prev).length === 0 ? prev : {}));
        }
        // ⚖️ B47 fix 8 — THE HOLD ENDS HERE, ON THIS LINE, AND NOT IN `finally`.
        // The read below is still awaited, so `finally` now runs when the LATE
        // answer lands — which on a socket that never answers is never. Fix 6's
        // whole claim is that the blank cannot outlive 10 s, so `loading` is
        // released explicitly at the deadline. It is the same `false` the
        // `finally` would have set, so nothing downstream meets a new shape,
        // and the `finally`'s own `setLoading(false)` is then a no-op (React
        // bails on an identical value) — the one thing it must never be is the
        // only place this happens.
        setLoading(false);
        // ⚖️ B47 fix 8 (round-36 UX 1) — AND THE READ GOES ON. Nothing was
        // cancelled, so a row answering at 12 s is a row that LOADED: the app
        // used to drop it on the floor and repeat "didn't load" on every press,
        // on exactly the slow link where supabase-js had one attempt left. If
        // it answers and it is still the newest read for this account (the
        // sequence token, and fix 6b's `rawFor`, are what make that safe), it
        // lands below as an on-time read would and `loadFailed` clears. If it
        // FAILS instead, the state is already the failed one this branch just
        // set — a read that timed out and then failed never wipes a row in hand
        // for the same account, and never says anything twice.
        try {
          settled = await pending;
        } catch {
          return;
        }
        if (seq !== readSeq.current) return;
        // ⚖️ B47 fix 9 (round-46 critic S3) — AND A LATE FAILURE ARRIVES AS A
        // VALUE, not as a throw. postgrest-js without `.throwOnError()`
        // RESOLVES a dead socket as `{ data: null, error, status: 0 }`, so the
        // catch above is dead against the real client: the resolved error fell
        // through to `if (error) throw error` below, into the outer catch, and
        // wiped `raw`, nulled the stamp and logged a second time — the exact
        // three things the deadline branch above had just decided not to do,
        // and against the founder's rule that a timed-out read never wipes a
        // row in hand for the same account. A late failure says nothing, in
        // either shape it can arrive in.
        if (settled.error) return;
      }
      const { data, error } = settled;
      if (error) throw error;
      const ctx = {
        ...((data?.context_data as Record<string, unknown> | null) ?? {}),
      };
      // Defensive backfill of Phase 7 canonical JSON signals from the
      // top-level columns. JSON wins when present; only fills in when
      // missing (preserves Phase 7 writes).
      if (data?.onboarding_completed === true && !ctx._onboarding_completed_at) {
        ctx._onboarding_completed = true;
        // 'legacy' is a truthy non-empty string — both the Today gate
        // and Onboarding guard treat `_onboarding_completed_at` as a
        // boolean truthy check, so any non-empty value works. Using a
        // marker rather than a synthesized date avoids implying a
        // specific completion date we don't actually know.
        ctx._onboarding_completed_at = 'legacy';
      }
      if (data?.journey && !ctx._journey) {
        ctx._journey = data.journey;
      }
      if (data?.parq_completed === true && !ctx._parq_completed) {
        ctx._parq_completed = true;
      }
      // ⚖️ B47 fix 6b — this row is this account's, and `raw` now says so.
      stampRawFor(userId);
      setRaw(ctx);
      setLoadFailed(false);
    } catch (err) {
      if (seq !== readSeq.current) return;
      // eslint-disable-next-line no-console
      console.warn('[athlete-context] load failed:', err);
      stampRawFor(null);
      setRaw({});
      // Empty-because-UNKNOWN (offline / network / RLS hiccup) — consumers
      // that route on missing context must not treat this as a new athlete.
      setLoadFailed(true);
    } finally {
      if (deadline !== undefined) clearTimeout(deadline);
      if (seq === readSeq.current) setLoading(false);
    }
  }, [supabase, userId, stampRawFor]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const mergeContext = useCallback(
    (updates: Record<string, unknown>) => {
      if (!updates || typeof updates !== 'object' || Object.keys(updates).length === 0) return;

      // Deep merge — vanilla pattern preserves coach_visible /
      // coach_private nested objects.
      const merged: Record<string, unknown> = { ...rawRef.current };
      for (const [k, v] of Object.entries(updates)) {
        if (
          (k === 'coach_visible' || k === 'coach_private') &&
          typeof v === 'object' &&
          v !== null
        ) {
          const existing = (merged[k] as Record<string, unknown> | undefined) ?? {};
          merged[k] = { ...existing, ...(v as Record<string, unknown>) };
        } else {
          merged[k] = v;
        }
      }
      setRaw(merged);
      // B148 — patch-only: send the delta, never the full blob.
      void persist({ patch: updates });
    },
    [persist],
  );

  const removePill = useCallback(
    (key: string) => {
      const next: Record<string, unknown> = { ...rawRef.current };
      const candidates = [
        key,
        key.toLowerCase(),
        key.charAt(0).toLowerCase() + key.slice(1),
        key.replace(/\s+/g, '_').toLowerCase(),
        key.replace(/\s+/g, ''),
      ];
      const removed: string[] = [];
      for (const c of candidates) {
        if (c in next) {
          delete next[c];
          removed.push(c);
        }
      }
      // B475 fix round 1 — the weekly figure lives under more than one key
      // (`weekly_mileage`, the band id, the exact entry, vanilla aliases) and the
      // pill shows one of them. Removing it removes the figure: the athlete took
      // it away, so the coaches must not keep it under another key. Each key is
      // deleted by its STORED spelling (the server deletes exact names only).
      if (isWeeklyMileageFigureKey(key)) {
        for (const stored of Object.keys(next)) {
          if (
            isWeeklyMileageFigureKey(stored) ||
            isWeeklyMileageBandKey(stored) ||
            isWeeklyMileageRepeatKey(stored)
          ) {
            delete next[stored];
            removed.push(stored);
          }
        }
      }
      setRaw(next);
      // Explicit deletion — `removeKeys` so the merge path can express it (a
      // pure additive merge can't). B148 — patch-only, no `context` key.
      void persist({ removeKeys: removed });
    },
    [persist],
  );

  const value = useMemo<UseAthleteContextResult>(() => {
    if (!supabase || !userId) return EMPTY_RESULT;

    // Flatten coach_visible into the top level for pill rendering — the
    // dedupe handles canonical-key collisions. We MUST NOT include
    // coach_private (private coach metadata) — its raw key is
    // hard-excluded by dedupePills, but we also drop it here for
    // belt-and-suspenders defense.
    const flattened: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(raw)) {
      const lk = k.toLowerCase();
      if (lk === 'coach_private') continue;
      if (lk === 'coach_visible') continue;
      flattened[k] = v;
    }
    const coachVisible = raw.coach_visible;
    if (coachVisible && typeof coachVisible === 'object' && !Array.isArray(coachVisible)) {
      Object.assign(flattened, coachVisible as Record<string, unknown>);
    }

    return {
      pills: dedupePills(flattened),
      raw,
      loading,
      loadFailed,
      // ⚖️ B47 fix 9 (B160) — the attribution fix 6b kept to itself. Derived per
      // render from the stamp the read wrote, so the switch commit (the stamp
      // still the previous account's, `userId` already the new one) reads false
      // without waiting for any effect.
      rawIsMine: rawOwner !== null && rawOwner === userId,
      mergeContext,
      removePill,
      reload,
    };
  }, [supabase, userId, raw, rawOwner, loading, loadFailed, mergeContext, removePill, reload]);

  return (
    <AthleteContextContext.Provider value={value}>{children}</AthleteContextContext.Provider>
  );
}

// ─────────────────────────────────────────────────────────────
// Consumer hook (unchanged signature)
// ─────────────────────────────────────────────────────────────

/**
 * Returns the shared athlete-context state. Same signature as the
 * pre-Phase-6b1.5-PR-2 hook so existing consumers (settings, today,
 * chat, onboarding, plan, races) keep working without changes.
 *
 * Outside `<AthleteContextProvider>` (e.g., isolated tests), returns
 * the no-op `EMPTY_RESULT` so consumers don't crash. Production
 * always renders inside the Provider via `main.tsx`.
 */
export function useAthleteContext(): UseAthleteContextResult {
  return useContext(AthleteContextContext);
}
